// The "Where are you stuck?" scorer, as the prototypes compute it: seven confidence scores become a targeted
// plan, a readiness percentage and a rung on the ladder. Pure, so the page's wording can be specified.

const TARGET = 4

// the prototype's djb2 hash: every score feeds it, so changing any one score reshapes the wording
const hash = (text) => {
  let h = 5381
  for (let i = 0; i < text.length; i += 1) h = ((h << 5) + h + text.charCodeAt(i)) | 0
  return Math.abs(h)
}

const capitalise = (text) => text.charAt(0).toUpperCase() + text.slice(1)

/** The example scores a page starts from, by function key. */
export const startScores = (scorer) => Object.fromEntries(scorer.areas.map((area) => [area.key, area.start]))

/** Which rung of the ladder a weighted stage score lands on, 1 to 5. */
export const rungOf = (stageScore) =>
  stageScore <= 1.9 ? 1 : stageScore <= 2.7 ? 2 : stageScore <= 3.5 ? 3 : stageScore <= 4.3 ? 4 : 5

/**
 * The result for a set of scores.
 *
 * @param scorer  the page's scorer copy: areas, weights, stages, the lines the result is built from and `plan`,
 *                which writes the page's own 30-day lines from the gaps and the stage
 * @param scores  the visitor's confidence in each function, 1 to 5, by key
 * @returns headline, bullets ([label, text]), pct, rung, stage, nextStage, focus (keys) and plan (the lines)
 */
export const assess = (scorer, scores) => {
  const { areas, priority, weights, stageWeights, stages, link, asset, problem, cost } = scorer
  const { headlines, tactics, strong, labels, partly, tiedTo } = scorer
  const total = areas.length
  const sum = areas.reduce((acc, area) => acc + scores[area.key], 0)

  const seed = hash(areas.map((area) => scores[area.key]).join('-'))
  const pick = (options, offset) => options[(Math.floor(seed / 7 ** offset) + offset * 3) % options.length]
  const drag = (key) => Math.max(0, TARGET - scores[key]) * weights[key]
  const tier = (key) => (scores[key] <= 2 ? 'low' : 'mid')
  const byPriority = (a, b) => priority.indexOf(a) - priority.indexOf(b)
  const name = (key) => areas.find((area) => area.key === key).name

  const gaps = priority.filter((key) => scores[key] < TARGET).sort((a, b) => drag(b) - drag(a) || byPriority(a, b))
  const focus = gaps.slice(0, 3)
  const lowest = [...priority].sort((a, b) => scores[a] - scores[b] || byPriority(a, b))[0]
  const best = [...priority].sort((a, b) => scores[b] - scores[a] || byPriority(a, b))[0]
  const [g0, g1] = focus.length ? focus : [lowest]
  const atTarget = areas.filter((area) => scores[area.key] >= TARGET).length

  const stageScore = Object.entries(stageWeights).reduce((acc, [key, weight]) => acc + weight * scores[key], 0)
  const rung = rungOf(stageScore)
  const stage = stages[rung - 1]
  const nextStage = rung < 5 ? stages[rung] : null

  const earned = areas.reduce((acc, area) => acc + weights[area.key] * (scores[area.key] - 1), 0)
  const possible = areas.reduce((acc, area) => acc + weights[area.key] * TARGET, 0)
  const pct = Math.round((100 * earned) / possible)

  const partial = scores[g0] === 3 ? partly : ''
  let headline
  if (!gaps.length) headline = pick(headlines.strong, 0)
  else if (!g1) headline = `${pick(headlines.one, 0)}${partial}${problem[g0]}.`
  else headline = `${pick(headlines.two, 0)}${partial}${problem[g0]}${tiedTo}${problem[g1]}.`

  const bullets = [[labels.cost, cost[rung - 1]]]
  if (gaps.length) {
    bullets.push([pick(labels.gap, 1), `${name(g0)}: ${link[g0][tier(g0)]}.`])
    if (g1) bullets.push([pick(labels.feeding, 2), `${name(g1)}: ${link[g1][tier(g1)]}.`])
    bullets.push([pick(tactics.labels, 3), g1 ? tactics.two : tactics.one])
    if (scores[best] >= TARGET) bullets.push([pick(labels.buildOn, 4), `${capitalise(asset[best])}.`])
    else if (sum / total <= 1.7) bullets.push([labels.goodNews, labels.start])
    else if (atTarget > 0) bullets.push([labels.closer, labels.atTarget(atTarget, total)])
    else bullets.push([labels.goodNews, labels.reach])
  } else {
    bullets.push([labels.risk, strong.risk])
    bullets.push([labels.fix, strong.fix])
  }

  const plan = scorer.plan({ gaps, g0, g1, focus, rung, stage, nextStage, name })

  return { headline, bullets, pct, rung, stage, nextStage, focus, plan }
}
